import { FC, ReactNode, useMemo, useEffect, useCallback } from 'react'
import {
  ConnectionProvider,
  WalletProvider as SolanaWalletProvider,
  useWallet,
} from '@solana/wallet-adapter-react'
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui'
import {
  PhantomWalletAdapter,
  SolflareWalletAdapter,
} from '@solana/wallet-adapter-wallets'
import {
  SolanaMobileWalletAdapter,
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
} from '@solana-mobile/wallet-adapter-mobile'
import { clusterApiUrl } from '@solana/web3.js'
import type { Adapter } from '@solana/wallet-adapter-base'
import { useWalletStore } from '@/store'

// Import wallet adapter styles
import '@solana/wallet-adapter-react-ui/styles.css'

interface Props {
  children: ReactNode
}

// App identity for Solana Mobile
const APP_IDENTITY = {
  name: 'Trench Quest',
  uri: 'https://trench-quest.web.app',
  icon: '/favicon.svg',
}

// Inner component that syncs wallet state to our store
const WalletStateSync: FC<{ children: ReactNode }> = ({ children }) => {
  const { connected, connecting, publicKey, disconnect, signMessage, wallet } = useWallet()
  const {
    setConnected,
    setConnecting,
    setPublicKey,
    setSignMessage,
    setDisconnect
  } = useWalletStore()

  // Log adapter events for debugging
  useEffect(() => {
    if (!wallet?.adapter) return

    const adapter = wallet.adapter
    console.log('[WalletSync] Setting up adapter listeners for:', adapter.name)

    const onConnect = () => {
      const pk = adapter.publicKey?.toBase58() || 'null';
      console.log('[WalletSync] Adapter CONNECT event, publicKey:', pk);
      if (typeof window !== 'undefined') {
        (window as any).__adapterEvent = 'connect: ' + pk;
      }
    }

    const onDisconnect = () => {
      console.log('[WalletSync] Adapter DISCONNECT event');
      if (typeof window !== 'undefined') {
        (window as any).__adapterEvent = 'disconnect';
      }
    }

    const onAdapterError = (e: Error) => {
      console.error('[WalletSync] Adapter ERROR event:', e.name, e.message);
      if (typeof window !== 'undefined') {
        (window as any).__adapterEvent = 'error: ' + e.message;
        (window as any).__walletError = e.message;
      }
    }

    const onReadyStateChange = (readyState: any) => {
      console.log('[WalletSync] Adapter READY_STATE_CHANGE:', readyState)
      if (typeof window !== 'undefined') {
        (window as any).__adapterEvent = 'readyState: ' + String(readyState)
      }
    }

    adapter.on('connect', onConnect)
    adapter.on('disconnect', onDisconnect)
    adapter.on('error', onAdapterError)
    adapter.on('readyStateChange', onReadyStateChange)

    // Log current adapter state
    console.log('[WalletSync] Adapter state:', {
      name: adapter.name,
      publicKey: adapter.publicKey?.toBase58(),
      connected: adapter.connected,
      readyState: adapter.readyState,
    })

    return () => {
      adapter.off('connect', onConnect)
      adapter.off('disconnect', onDisconnect)
      adapter.off('error', onAdapterError)
      adapter.off('readyStateChange', onReadyStateChange)
    }
  }, [wallet])

  useEffect(() => {
    console.log('[WalletSync] connected changed:', connected)
    setConnected(connected)
  }, [connected, setConnected])

  useEffect(() => {
    console.log('[WalletSync] connecting changed:', connecting)
    setConnecting(connecting)
  }, [connecting, setConnecting])

  useEffect(() => {
    console.log('[WalletSync] publicKey changed:', publicKey?.toBase58() ?? null)
    setPublicKey(publicKey?.toBase58() ?? null)
  }, [publicKey, setPublicKey])

  useEffect(() => {
    setSignMessage(signMessage ?? null)
  }, [signMessage, setSignMessage])

  useEffect(() => {
    setDisconnect(disconnect)
  }, [disconnect, setDisconnect])

  return <>{children}</>
}

export const WalletProvider: FC<Props> = ({ children }) => {
  // Configure for Solana mainnet
  const endpoint = useMemo(() => clusterApiUrl('mainnet-beta'), [])

  // Detect if running on mobile
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  }, [])

  // Configure wallets - add mobile adapter on mobile devices
  const wallets = useMemo(() => {
    const walletList: Adapter[] = [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
    ]

    // Add Solana Mobile Wallet Adapter on mobile devices (for Seeker, Phantom Mobile, etc.)
    if (isMobile) {
      console.log('[WalletProvider] Adding MWA adapter for mobile')
      walletList.unshift(
        new SolanaMobileWalletAdapter({
          addressSelector: createDefaultAddressSelector(),
          appIdentity: APP_IDENTITY,
          authorizationResultCache: createDefaultAuthorizationResultCache(),
          cluster: 'mainnet-beta',
          onWalletNotFound: async () => {
            console.error('[MWA] No mobile wallet found')
            if (typeof window !== 'undefined') {
              (window as any).__walletError = 'No mobile wallet found'
            }
          },
        })
      )
    } else {
      console.log('[WalletProvider] Desktop mode - no MWA adapter')
    }

    return walletList
  }, [isMobile])

  // Handle wallet errors - don't crash the app
  const onError = useCallback((error: Error) => {
    console.error('[WalletProvider] Wallet error:', error.name, error.message, error)
    // Store error for debug display
    if (typeof window !== 'undefined') {
      (window as any).__walletError = error.message
    }
  }, [])

  return (
    <ConnectionProvider endpoint={endpoint}>
      <SolanaWalletProvider
        wallets={wallets}
        autoConnect
        onError={onError}
      >
        <WalletModalProvider>
          <WalletStateSync>{children}</WalletStateSync>
        </WalletModalProvider>
      </SolanaWalletProvider>
    </ConnectionProvider>
  )
}
